import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAccount } from 'wagmi';

import AnimatedTransition from 'shared/components/ui/AnimatedTransition/AnimatedTransition';
import BetInput from 'shared/components/ui/BetInput/BetInput';
import GuysLoader from 'shared/components/ui/Loaders/GuysLoader/GuysLoader';
import PrimaryButton from 'shared/components/ui/PrimaryButton/PrimaryButton';
import BetButton from 'shared/components/ui/SecondaryBtn/BetButton';
import SecondaryBtn from 'shared/components/ui/SecondaryBtn/SecondaryBtn';
import Select, { selectOption } from 'shared/components/ui/Select/Select';
import TextWithLeftColorLine from 'shared/components/ui/TextWithLeftColorLine/TextWithLeftColorLine';
import { Switch } from 'shared/components/ui/switch/switch';
import { allowanceMessages, creatingMessages } from 'shared/constants/text-loaders';
import { useBalance } from 'shared/hooks/useBalance';
import { useCurrenciesStore } from 'shared/store/currenciesStore';
import { useStartGameStore } from 'shared/store/start-game-store';
import { formatNumber } from 'shared/web3/utils';

const regex = /^-?\d*\.?\d+$/;

interface CreateGameSectionProps {
  approveAllowanceHandler: (fullAllowance: boolean, bet: number) => void;
  startGameHandler: (bet: number) => void;
}

export const CreateGameSection = ({
  approveAllowanceHandler,
  startGameHandler,
}: CreateGameSectionProps) => {
  const { t } = useTranslation();
  const [bet, setBet] = useState('5');
  const [betIsValid, setBetIsValid] = useState(true);
  const { address, chainId } = useAccount();
  const [fullAllowance, setFullAllowance] = useState(true);

  const { setUserBet, currencyForSelect, selectedCurrency, setSelectedCurrency, userBets } =
    useCurrenciesStore();
  const { currentAllowance, isFetching, isApproving, txHash, isStarting } = useStartGameStore();

  useEffect(() => {
    if (selectedCurrency && chainId) {
      setBet(String(userBets?.[chainId]?.[selectedCurrency?.symbol] ?? selectedCurrency.bet[1]));
    }
  }, [selectedCurrency, chainId]);

  const handleSelectCurrency = (currency: selectOption) => {
    setSelectedCurrency(currency.value as string);
  };
  const handleSetBetInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value;
    value = value.replace(/[^0-9.,]/g, '');
    value = value.replace(/,/g, '.');
    value = value.replace(/(\..*)\./g, '$1');
    value = value.replace(/^\./, '');
    if (value.startsWith('0') && value[1] && !['.', ','].includes(value[1])) {
      value = `0.${value.slice(1)}`;
    }
    let moreThanMin = +value >= selectedCurrency!.minBet;
    let valid = regex.test(value) && moreThanMin;
    setBetIsValid(valid);
    if (valid && chainId) {
      setUserBet(chainId, +value, selectedCurrency!.symbol);
    }
    setBet(value);
  };

  const handleSetBet = (value: string) => {
    const regex = /^-?\d*\.?\d+$/;
    setBetIsValid(regex.test(value));
    setBet(value);
    if (chainId) setUserBet(chainId, +value, selectedCurrency!.symbol);
  };
  const { balance } = useBalance({
    currencyAddress: selectedCurrency?.address,
    walletAddress: address as `0x${string}`,
  });

  const getAuthorizeDisable = () => {
    return (Number(currentAllowance) >= +bet && betIsValid) || isApproving || isFetching || !bet;
  };
  if (!selectedCurrency) return null;

  return (
    <div className="relative z-10 mb-5">
      <div className="font-bold text-[20px] lg:text-[52px] gradient-title">
        {t('modals.setBet.title')}
      </div>
      <div className="p-[20px] sm:p-[30px] relative w-full xlg:max-w-[578px] lg:min-w-[578px] bg-[linear-gradient(0deg,#080540,#080540),radial-gradient(50%_50%_at_50%_50%,rgba(0,11,255,0.3)_0%,rgba(1,0,29,0.3)_100%)] border-[1px] border-[#202066] rounded-[8px] lg:rounded-[16px] lg:border-[3px] mt-[12px]">
        <AnimatedTransition show={isApproving || isStarting} className={' z-50'}>
          <GuysLoader
            hash={txHash}
            messages={isApproving ? allowanceMessages : creatingMessages}
            className="absolute top-0 left-0 rounded-[4px] sm:rounded-[14px] w-full h-full bg-[#000014D9] bg-opacity-85 z-50"
          />
        </AnimatedTransition>
        <TextWithLeftColorLine colorClass="bg-green" className="mb-[25px]">
          <p className="text-white text-[12px] sm:text-[16px]">{t('modals.setBet.select')}</p>
        </TextWithLeftColorLine>
        <div className="flex w-full mb-[25px] items-start justify-between">
          <div className="w-[70%] sm:flex-grow flex flex-col  mr-[10px] sm:mr-[22px]">
            <BetInput
              value={bet.toString()}
              valueHandler={handleSetBetInput}
              type="number"
              className="sm:flex-grow"
              balance={balance ?? 0}
              disabled={isApproving || isFetching}
            />
          </div>
          <Select
            className="flex-grow"
            options={currencyForSelect}
            selectedOption={{
              value: selectedCurrency.symbol,
              label: selectedCurrency.symbol,
            }}
            onSelect={handleSelectCurrency}
            disabled={isApproving || isFetching}
          />
        </div>
        <div className="flex  gap-[14px] pb-[20px] border-border border-b-2 mb-[20px]">
          {selectedCurrency?.bet &&
            selectedCurrency.bet.map((betValue, index) => (
              <BetButton
                key={index}
                className={`flex-grow`}
                text={`${formatNumber(+betValue)} ${selectedCurrency.symbol}`}
                value={betValue}
                onClick={() => handleSetBet(betValue)}
                disabled={isApproving || isFetching}
              />
            ))}
        </div>
        <AnimatedTransition show={!getAuthorizeDisable()}>
          <div className="pt-4 gap-2  flex items-center">
            <p className="text-[14px] text-[#C1C0CC] border-r-[1px] pr-3 mr-3 border-border">
              {t('modals.setBet.plan')}
            </p>
            <Switch
              className="ml-2"
              onCheckedChange={() => {
                setFullAllowance(!fullAllowance);
              }}
              checked={fullAllowance}
            />
          </div>
        </AnimatedTransition>
        <div className={'grid grid-cols-2 gap-4 mt-4 z-0'}>
          <PrimaryButton
            text={t('common.authorize')}
            disabled={getAuthorizeDisable()}
            onClick={() => {
              approveAllowanceHandler(fullAllowance, +bet);
            }}
          />
          <PrimaryButton
            text={'common.startGame'}
            disabled={
              (Number(currentAllowance) < +bet && betIsValid) ||
              isApproving ||
              isFetching ||
              (balance ?? 0) < +bet
            }
            onClick={() => {
              startGameHandler(+bet);
            }}
          />
        </div>
      </div>
    </div>
  );
};
